import { useState } from "react";
import axios from "axios";
import { Spinner } from "./PageLoader";
import config from "./config";

// A "blank" banner is the placeholder asset — shown grayscale like the community
// hero so default headers read as monochrome rather than a flat colour block.
const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);

/**
 * Change-password modal for a signed-in community member.
 * Verifies the current password server-side and updates to a new one.
 */
function ChangePasswordModal({ token, member, primary = "#0274F7", banner, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const save = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!current || !next || !confirm) {
      setMsg({ type: "error", text: "Please fill in all fields." });
      return;
    }
    if (next.length < 6) {
      setMsg({ type: "error", text: "New password must be at least 6 characters." });
      return;
    }
    if (next !== confirm) {
      setMsg({ type: "error", text: "New passwords do not match." });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-member-change-password`, {
        community_token: token,
        member_token: member?.auth_token,
        current_password: current,
        new_password: next,
      });
      if (res.data?.error === 0) {
        setMsg({ type: "success", text: res.data?.msg || "Password changed." });
        setCurrent("");
        setNext("");
        setConfirm("");
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Could not change password." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Branded header: community banner in the background, tinted with the
            selected theme colour so the text on top stays readable. */}
        <div className="relative px-6 pt-8 pb-7 text-center overflow-hidden bg-gray-900">
          {banner && (
            <div
              aria-hidden="true"
              className={`absolute inset-0 z-0 bg-center bg-cover${
                isEmptyBanner(banner) ? " grayscale" : ""
              }`}
              style={{ backgroundImage: `url("${banner}")` }}
            />
          )}
          {/* Light theme-colour overlay over the (grayscale) banner — a soft
              brand tint, slightly deeper at the bottom so the text stays legible. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{
              background: "linear-gradient(160deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.70) 100%)",
            }}
          />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white text-lg leading-none transition"
          >
            ×
          </button>
          <div className="relative z-10 w-16 h-16 mx-auto rounded-2xl bg-white shadow-md flex items-center justify-center" style={{ color: primary }}>
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-white text-xl font-bold mt-4 relative z-10" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>Change password</h3>
          <p className="text-white/95 text-sm mt-1 relative z-10" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>{member?.email}</p>
        </div>

        <form onSubmit={save} className="p-6 space-y-4">
          {msg.text && (
            <div
              className={`text-sm rounded-lg px-3.5 py-2.5 ${
                msg.type === "success"
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-600 border border-red-100"
              }`}
            >
              {msg.text}
            </div>
          )}

          <Field label="Current password" value={current} onChange={setCurrent} disabled={saving} primary={primary} />
          <Field label="New password" value={next} onChange={setNext} disabled={saving} primary={primary} />
          <Field label="Confirm new password" value={confirm} onChange={setConfirm} disabled={saving} primary={primary} />

          <button
            type="submit"
            disabled={saving}
            style={{ backgroundColor: primary }}
            className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition disabled:opacity-60 shadow-sm"
          >
            {saving && (
              <Spinner size={16} />
            )}
            {saving ? "Saving…" : "Change password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, primary }) {
  const [focused, setFocused] = useState(false);
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 block">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          disabled={disabled}
          autoComplete="off"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border px-3.5 py-2.5 pr-11 text-sm text-gray-800 outline-none"
          style={{
            borderColor: focused ? primary : "#e5e7eb",
            boxShadow: focused ? `0 0 0 3px ${primary}22` : "none",
          }}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          {show ? (
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 4.2A9.6 9.6 0 0112 4c5 0 9 4 10 8a11 11 0 01-2.2 3.4M6.1 6.1A11 11 0 002 12c1 4 5 8 10 8 1.4 0 2.7-.3 3.9-.8" />
            </svg>
          ) : (
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </label>
  );
}

export default ChangePasswordModal;
